'use client';
import { X } from 'lucide-react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/navigation';
import { Suspense, useCallback, useLayoutEffect, useState } from 'react';

import { RuntimeEnvProvider } from '@/components/runtime-env';
import { Dialog, DialogClose, DialogContent, DialogTitle } from '@/components/ui/dialog';

const CalculationMapComponent = dynamic(() => import('@/freight/map/CalculationMapComponent'), { ssr: false });

export default function PlanMapModal({ resultId, planId }: { resultId: string; planId: string }) {
    const [height, setHeight] = useState(0);
    const [ref, setRef] = useState<HTMLDivElement | null>(null);

    const updateHeight = useCallback(
        (entries: ResizeObserverEntry[]) => {
            for (const entry of entries) {
                setHeight(entry.target.clientHeight);
            }
        },
        [setHeight]
    );

    useLayoutEffect(() => {
        if (ref) {
            const resizeObserver = new ResizeObserver(updateHeight);

            resizeObserver.observe(ref);

            return () => {
                resizeObserver.disconnect();
            };
        }
    }, [ref, updateHeight]);

    const router = useRouter();

    return (
        <Dialog
            open
            onOpenChange={(open) => {
                if (!open) {
                    router.back();
                }
            }}
        >
            <DialogContent className="p-0 pr-4 rounded-lg" noClose fullScreen>
                <DialogTitle className="hidden">Map</DialogTitle>

                <div
                    ref={(el) => {
                        setRef(el);
                    }}
                    className="h-modalH"
                >
                    <Suspense>
                        <RuntimeEnvProvider>
                            <CalculationMapComponent
                                resultId={resultId}
                                planId={planId}
                                mapHeight={height}
                                waitForHeight
                                rounded
                            />
                        </RuntimeEnvProvider>
                    </Suspense>
                </div>

                <DialogClose className="absolute -right-4 -top-4 rounded-full p-2 bg-frGrey-990 text-white ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
                    <X className="h-4 w-4" />
                    <span className="sr-only">Close</span>
                </DialogClose>
            </DialogContent>
        </Dialog>
    );
}
